// Kehidupan sungai (dipisah dari engine.js, perilaku identik): kilau arus adaptif zoom, armada perahu
// (speedboat + perahu nelayan dari katalog) dengan wake & lentera malam.
import { TILE_W, TILE_H } from "./iso";
import { hashN } from "./sky";
import { paintBuildingAt } from "./paint";
import { drawRoadTile, drawBridge } from "./road";

export function createRiver(c) {
  // c = { gridToScreen, cam, get atmos }
  const cam = c.cam, gridToScreen = c.gridToScreen;
  let ctx = null, atmos = null;
  let waterTilesCache = [];      // tile sungai (untuk efek mengalir per-frame)
  let waterSetCache = new Set(); // kunci "x,y" tile air (deteksi tile jembatan = jalan ∩ air)
  let waterFlowAxis = "x";       // sumbu aliran global: 'x' → kanan-bawah layar, 'y' → kiri-bawah
  let roadSetCache = new Set();  // untuk deteksi jembatan (perahu lewat di bawahnya)
  let boatLane = null;           // jalur tengah sungai { axis, lo, hi, lane, half }
  let boatDefCache = null;       // aset perahu dari katalog
  const boatLights = [];         // lentera perahu (posisi+alpha) untuk pendar malam di atas selubung
  const boatPos = [];            // posisi layar perahu frame ini (debug/uji otomatis)

  function setWater({ tiles, roadSet, axis, lane, defs }) {
    waterTilesCache = tiles; roadSetCache = roadSet; waterFlowAxis = axis; boatLane = lane;
    waterSetCache = new Set(tiles.map(w => w.x + "," + w.y));
    const boatCands = ["speedboat", "perahu_nelayan"].map(id => (defs || []).find(d => d.bid === id && d.paintCode)).filter(Boolean);
    boatDefCache = boatCands.length ? boatCands : null;
  }

  const isBridge = (x, y) => { const k = x + "," + y; return roadSetCache.has(k) && waterSetCache.has(k); };

  // Jarak (tile) perahu ke tile jembatan terdekat di lajurnya → perahu memudar saat masuk ke bawah jembatan
  // (bukan menghilang mendadak) dan tidak pernah tampak "menembus" dek.
  function redupJembatan(bgx, bgy) {
    const tx = Math.floor(bgx), ty = Math.floor(bgy);
    let dMin = Infinity;
    for (let d = -2; d <= 2; d++) {
      const x = boatLane.axis === "x" ? tx + d : tx, y = boatLane.axis === "x" ? ty : ty + d;
      if (!isBridge(x, y)) continue;
      const pusat = boatLane.axis === "x" ? x + 0.5 : y + 0.5, pos = boatLane.axis === "x" ? bgx + 0.5 : bgy + 0.5;
      dMin = Math.min(dMin, Math.abs(pos - pusat));
    }
    return dMin === Infinity ? 1 : Math.max(0, Math.min(1, (dMin - 0.6) / 0.6));
  }

  // Perahu sudah dilukis di atas dunia (termasuk jembatan) → lukis ulang tile jembatan di sekitarnya
  // sehingga dek & pagar kembali menutupi bagian perahu/buih/sorot yang menindihnya (perahu lewat DI BAWAH jembatan).
  function tutupJembatan(bgx, bgy, bx, by) {
    const tx = Math.floor(bgx), ty = Math.floor(bgy);
    const TW = TILE_W * cam.zoom, TH = TILE_H * cam.zoom, r = TW * 1.2;
    const has = (x, y) => roadSetCache.has(x + "," + y);
    for (let dx = -2; dx <= 2; dx++) for (let dy = -2; dy <= 2; dy++) {
      const x = tx + dx, y = ty + dy;
      if (!isBridge(x, y)) continue;
      const [cx, cy] = gridToScreen(x + 0.5, y + 0.5);
      if (Math.abs(cx - bx) > r || Math.abs(cy - by) > r) continue;
      drawRoadTile(ctx, x, y, cx, cy, TW, TH, has, [], waterSetCache);
      drawBridge(ctx, x, y, cx, cy, TW, TH, has);
    }
  }

  function drawFlow(t, W, H) {
    // ---- Efek air mengalir: kilau cahaya bergerak searah aliran sungai ----
    // Digambar per-frame (di atas dunia cache). Tile jembatan (jalan ∩ air)
    // DI-SKIP agar kilau TIDAK tampak menumpuk di atas geladak jembatan —
    // arus seharusnya "di bawah" jembatan, jadi tersembunyi di tile itu.
    // Saat ZOOM-OUT (zoom < 0.6) kilau diperkuat bertahap — lebih banyak tile berkilau,
    // tebal minimal ±1px, lebih panjang & terang — agar arus tetap terbaca dari jauh.
    // Pada zoom ≥ 0.6 semua faktor = 1 sehingga proporsi asli saat zoom-in tidak berubah.
    if (waterTilesCache.length && cam.zoom >= 0.09) {
      const wz = cam.zoom;
      const hwz = (TILE_W / 2) * wz, hhz = (TILE_H / 2) * wz;
      const far = Math.max(0, Math.min(1, (0.6 - wz) / 0.5));
      const gateMax = 0.3 + far * 0.4, aBoost = 1 + far * 1.2, lenMul = 1 + far * 1.1;
      ctx.save();
      ctx.lineCap = "butt";
      ctx.lineWidth = Math.max(0.5 + far * 0.8, hwz * 0.042);
      ctx.strokeStyle = "#E4F4FF";
      for (const w of waterTilesCache) {
        if (roadSetCache.has(w.x + "," + w.y)) continue; // di bawah jembatan — arus tersembunyi
        // kilau JARANG & ACAK: hanya ±30% tile yang punya, fase/kecepatan/posisi
        // melintang diacak per tile agar tidak beraturan
        const gate = hashN(w.x * 3.1 + w.y * 7.7);
        if (gate > gateMax) continue;
        const latJit = (hashN(w.x * 9.1 + w.y * 4.3) - 0.5) * 0.9; // offset melintang acak
        const spdJit = 0.6 + hashN(w.x * 6.7 + w.y * 8.9) * 0.9;   // kecepatan acak
        const ph0 = hashN(w.x * 5.3 + w.y * 2.9) * 3;              // fase acak
        const jx = waterFlowAxis === "x" ? 0 : latJit;
        const jy = waterFlowAxis === "x" ? latJit : 0;
        const [wcx, wcy] = gridToScreen(w.x + 0.5 + jx, w.y + 0.5 + jy);
        if (wcx < -90 || wcx > W + 90 || wcy < -90 || wcy > H + 90) continue;
        // sumbu aliran global di layar (mengalir ke arah bawah layar):
        // sumbu x grid → kanan-bawah, sumbu y grid → kiri-bawah
        let ax, ay;
        if (waterFlowAxis === "x") { ax = hwz; ay = hhz; }
        else { ax = -hwz; ay = hhz; }
        const alen = Math.hypot(ax, ay) || 1;
        const ux = ax / alen, uy = ay / alen;
        const n = gate < 0.12 ? 2 : 1; // sebagian kecil tile punya 2 kilau
        for (let k = 0; k < n; k++) {
          const s = ((t * 0.055 * spdJit + ph0 + k * 0.5) % 1);
          const off = (s - 0.5) * alen * 1.5;
          const px = wcx + ux * off, py = wcy + uy * off;
          const L = alen * 0.38 * lenMul;
          ctx.globalAlpha = Math.min(0.85, Math.sin(s * Math.PI) * 0.3 * aBoost);
          ctx.beginPath();
          ctx.moveTo(px - ux * L / 2, py - uy * L / 2);
          ctx.lineTo(px + ux * L / 2, py + uy * L / 2);
          ctx.stroke();
        }
      }
      ctx.restore();
    }
    // Ikan kecil melompat melawan arus (di atas kilau, di bawah perahu)

  }

  function drawBoats(at, W, H, on) {
    // ---- ARMADA PERAHU (aset Studio AI): 1 SPEEDBOAT cepat searah aliran + 4 PERAHU NELAYAN
    // pelan (2 arah) tersebar di lajur berbeda (fraksi dari setengah lebar sungai), ujung-ke-ujung
    // dengan fase awal berbeda. Memudar (fade) di kedua ujung sungai. Saat malam lentera menyala
    // (dikumpulkan ke boatLights, digambar di atas selubung malam).
    boatLights.length = 0; boatPos.length = 0;
    if (on && boatLane && boatDefCache) {
      const span = boatLane.hi - boatLane.lo;
      if (span >= 6) {
        // def: indeks aset (0 speedboat, 1 nelayan) · speed tile/dtk · lane fraksi -1..1 · fwd searah aliran · ph fase awal
        const BOATS = [
          { def: 0, speed: 0.62, lane: -0.6, fwd: true, ph: 0.0, wake: 1.3, wakeA: 0.45 },
          { def: 1, speed: 0.24, lane: 0.6, fwd: false, ph: 0.5, wake: 0.7, wakeA: 0.3 },
          { def: 1, speed: 0.2, lane: 0.2, fwd: false, ph: 0.12, wake: 0.6, wakeA: 0.28 },
          { def: 1, speed: 0.27, lane: -0.2, fwd: true, ph: 0.68, wake: 0.7, wakeA: 0.3 },
          { def: 1, speed: 0.22, lane: 0.95, fwd: true, ph: 0.3, wake: 0.6, wakeA: 0.28 }
        ];
        for (let bi = 0; bi < BOATS.length; bi++) {
          const B = BOATS[bi], fwd = B.fwd;
          const p = (at * B.speed + B.ph * span) % span;
          const pos = fwd ? boatLane.lo + p : boatLane.hi - p;
          const laneOff = B.lane * boatLane.half; // lajur masing-masing agar tidak bertabrakan
          const bgx = boatLane.axis === "x" ? pos : boatLane.lane + laneOff;
          const bgy = boatLane.axis === "x" ? boatLane.lane + laneOff : pos;
          // fade di kedua ujung sungai × memudar saat masuk ke bawah jembatan
          const alpha = Math.max(0, Math.min(1, p / 1.5, (span - p) / 1.5)) * redupJembatan(bgx, bgy);
          if (alpha <= 0.01) continue;
          const [bx, by0] = gridToScreen(bgx + 0.5, bgy + 0.5);
          const by = by0 + (B.def === 1 ? Math.sin(at * 1.6 + bi * 1.9) * 1.2 * cam.zoom : 0); // perahu nelayan terayun ombak
          if (bx < -80 || bx > W + 80 || by < -80 || by > H + 80) continue;
          // buih wake V di belakang buritan (speedboat lebih panjang)
          const hwz = (TILE_W / 2) * cam.zoom, hhz = (TILE_H / 2) * cam.zoom;
          let wux = boatLane.axis === "x" ? hwz : -hwz, wuy = hhz;
          const wl = Math.hypot(wux, wuy) || 1; wux /= wl; wuy /= wl;
          if (!fwd) { wux = -wux; wuy = -wuy; }
          const backAng = Math.atan2(-wuy, -wux);
          const wakeL = B.wake;
          ctx.save();
          ctx.strokeStyle = "#FFFFFF"; ctx.lineCap = "butt";
          ctx.lineWidth = Math.max(0.5, hwz * 0.03);
          for (const da of [-0.45, 0.45]) {
            const a2 = backAng + da;
            ctx.globalAlpha = alpha * B.wakeA;
            ctx.beginPath();
            ctx.moveTo(bx - wux * hwz * 0.3, by - wuy * hwz * 0.3);
            ctx.lineTo(bx - wux * hwz * 0.3 + Math.cos(a2) * hwz * wakeL, by - wuy * hwz * 0.3 + Math.sin(a2) * hwz * wakeL);
            ctx.stroke();
          }
          ctx.restore();
          // gambar perahu dari katalog (arah hadap mengikuti lajur)
          const dir = boatLane.axis === "x" ? (fwd ? "SE" : "NW") : (fwd ? "SW" : "NE");
          const boatDef = boatDefCache[Math.min(B.def, boatDefCache.length - 1)];
          ctx.save();
          ctx.globalAlpha = alpha;
          paintBuildingAt(ctx, { ...boatDef, category: "Kendaraan" }, bx, by, cam.zoom,
            { time: at + bi * 5, night: atmos.dark, dir });
          ctx.restore();
          tutupJembatan(bgx, bgy, bx, by);   // dek jembatan kembali menutupi perahu yang lewat di bawahnya
          boatPos.push({ x: bx, y: by, bi, def: B.def, alpha });
          if (atmos.dark > 0.15) boatLights.push({ x: bx, y: by, a: alpha });
        }
      }
    }
  }

  function drawLights(at) {
    // Lentera perahu saat gelap: pendar hangat + titik pijar + pantulan berkilau di air
    if (atmos.dark > 0.15 && boatLights.length) {
      const ni = (atmos.dark - 0.15) / 0.85, z = cam.zoom;
      for (const b of boatLights) {
        const a = ni * b.a;
        if (a <= 0.02) continue;
        const lx = b.x, ly = b.y - 9 * z;
        const g = ctx.createRadialGradient(lx, ly, 0, lx, ly, 26 * z);
        g.addColorStop(0, `rgba(255,214,130,${(0.55 * a).toFixed(3)})`);
        g.addColorStop(1, "rgba(255,214,130,0)");
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(lx, ly, 26 * z, 0, 7); ctx.fill();
        ctx.fillStyle = `rgba(255,246,200,${(0.95 * a).toFixed(3)})`;
        ctx.beginPath(); ctx.arc(lx, ly, Math.max(1, 2 * z), 0, 7); ctx.fill();
        ctx.strokeStyle = `rgba(255,214,130,${(0.4 * a).toFixed(3)})`;
        ctx.lineWidth = Math.max(0.8, 2.2 * z);
        for (let k = 0; k < 4; k++) {
          const yy = b.y + (7 + k * 6.5) * z + Math.sin(at * 3 + k * 1.7) * 1.4 * z;
          const hw = (7 - k * 1.3) * z + Math.sin(at * 2.3 + k) * 1.5 * z;
          ctx.beginPath(); ctx.moveTo(lx - hw, yy); ctx.lineTo(lx + hw, yy); ctx.stroke();
        }
      }
    }
  }

  return {
    setWater,
    drawFlow(g, t, W, H) { ctx = g; atmos = c.atmos; drawFlow(t, W, H); },
    drawBoats(g, at, W, H, on) { ctx = g; atmos = c.atmos; drawBoats(at, W, H, on); },
    drawLights(g, at) { ctx = g; atmos = c.atmos; drawLights(at); },
    boats: boatPos
  };
}
